import { useEffect, useState, type FormEvent } from "react";
import { ArrowLeft, ArrowRight, Eye, EyeOff, Globe2, LockKeyhole } from "lucide-react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { QoricanchaMark } from "../components/PublicHeader";
import { ADMIN_HOST, PUBLIC_ORIGIN, safeStaffDestination } from "../../shared/hosts";
import { ApiError } from "../lib/api";

const copy = {
  en: {
    back: "Guest website", workspace: "Reception", welcome: "Staff sign in", intro: "Your day at Sight Qoricancha starts here.",
    email: "Email address", password: "Password", show: "Show password", hide: "Hide password", submit: "Sign in", busy: "Signing in…",
    aside: "A good stay starts with a warm welcome.", caption: "Santo Domingo 220 · Cusco, Perú", note: "Use your staff account to manage the hostel.",
    help: "Need access or a password reset? Contact the person who manages your staff account.",
    invalid: "That email and password do not match. Please try again.", limited: "Too many sign-in attempts. Please wait 15 minutes and try again.",
    unavailable: "We could not sign you in. Check your connection and try again.", caps: "Caps Lock is on", loading: "Checking your session…",
  },
  es: {
    back: "Sitio del hostal", workspace: "Recepción", welcome: "Acceso del personal", intro: "Tu día en Sight Qoricancha empieza aquí.",
    email: "Correo electrónico", password: "Contraseña", show: "Mostrar contraseña", hide: "Ocultar contraseña", submit: "Ingresar", busy: "Ingresando…",
    aside: "Una buena estadía empieza con una cálida bienvenida.", caption: "Santo Domingo 220 · Cusco, Perú", note: "Usa tu cuenta de personal para administrar el hostal.",
    help: "¿Necesitas acceso o restablecer tu contraseña? Contacta a la persona que administra tu cuenta de personal.",
    invalid: "El correo y la contraseña no coinciden. Inténtalo de nuevo.", limited: "Demasiados intentos de acceso. Espera 15 minutos e inténtalo de nuevo.",
    unavailable: "No pudimos iniciar tu sesión. Revisa tu conexión e inténtalo de nuevo.", caps: "Bloq Mayús está activado", loading: "Verificando tu sesión…",
  },
};

export function LoginPage() {
  const { user, loading, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguage] = useState<"en" | "es">(() => {
    const requested = new URLSearchParams(location.search).get("lang");
    if (requested === "en" || requested === "es") return requested;
    try { return localStorage.getItem("staff-language") === "es" ? "es" : "en"; } catch { return "en"; }
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const t = copy[language];
  const destination = safeStaffDestination((location.state as { from?: string } | null)?.from);
  const website = `${window.location.hostname === ADMIN_HOST ? PUBLIC_ORIGIN : ""}${language === "es" ? "/es" : "/"}`;

  useEffect(() => {
    document.documentElement.lang = language;
    document.title = `${copy[language].welcome} · Sight Qoricancha`;
    try { localStorage.setItem("staff-language", language); } catch { /* Sign-in works without local storage. */ }
  }, [language]);

  if (loading) return <div className="app-loading" role="status">{t.loading}</div>;
  if (user) return <Navigate to={destination} replace />;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError(""); setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      await login(String(form.get("email")).trim().toLowerCase(), String(form.get("password")));
      navigate(destination, { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError && caught.status === 401 ? "invalid"
        : caught instanceof ApiError && caught.status === 429 ? "limited" : "unavailable");
    } finally { setBusy(false); }
  }

  return <div className="login-page">
    <aside className="login-aside">
      <div className="login-brand"><QoricanchaMark /><span>Sight<strong>Qoricancha</strong><small>Hostel · Cusco</small></span></div>
      <div className="login-scene"><img src="/media/qoricancha-view.jpg" alt="" /><span>{t.caption}</span></div>
      <p className="login-aside-message">{t.aside}</p>
    </aside>
    <main className="login-main">
      <header className="login-topbar"><a href={website} className="back-link"><ArrowLeft size={16} />{t.back}</a>
        <div className="language-picker" aria-label="Language / Idioma"><Globe2 size={16} aria-hidden="true" />
          <button type="button" lang="en" aria-pressed={language === "en"} className={language === "en" ? "active" : ""} onClick={() => setLanguage("en")}>English</button><span aria-hidden="true">/</span>
          <button type="button" lang="es" aria-pressed={language === "es"} className={language === "es" ? "active" : ""} onClick={() => setLanguage("es")}>Español</button>
        </div>
      </header>
      <form className="login-form" onSubmit={submit} aria-busy={busy}>
        <span className="eyebrow"><LockKeyhole size={15} />{t.workspace}</span><h1>{t.welcome}</h1><p>{t.intro}</p>
        <label className="field full"><span>{t.email}</span><input type="email" name="email" autoComplete="username" autoCapitalize="none" spellCheck={false} inputMode="email" maxLength={254} required /></label>
        <label className="field full"><span>{t.password}</span><span className="password-field"><input type={visible ? "text" : "password"} name="password" aria-label={t.password} autoComplete="current-password" maxLength={128} required onKeyUp={(event) => setCapsLock(event.getModifierState("CapsLock"))} onBlur={() => setCapsLock(false)} /><button type="button" onClick={() => setVisible(!visible)} aria-label={visible ? t.hide : t.show} aria-pressed={visible}>{visible ? <EyeOff size={20} /> : <Eye size={20} />}</button></span></label>
        {capsLock && <small className="login-caps" role="status">{t.caps}</small>}
        {error && <p className="form-error" role="alert">{t[error as "invalid" | "limited" | "unavailable"]}</p>}
        <button className="button button-primary full" disabled={busy}>{busy ? t.busy : t.submit}<ArrowRight size={18} /></button>
        <small className="login-help">{t.help}</small>
      </form>
      <footer className="login-footer">{t.note}</footer>
    </main>
  </div>;
}
